import { X } from "lucide-react-native";
import { FC, useCallback, useMemo, useState } from "react";

import { Box, HStack, Pressable, Skeleton, Text } from "@/components/ui";
import { useLocalization } from "@/hooks";
import {
  DemographicDistribution,
  DemographicValue,
  DemographicValueValue,
} from "@/types/resources";
import DemographicDistributionEditor from "./DemographicDistributionEditor";

interface DemographicSettingEditorProps {
  availableOptions: DemographicValue[];
  disabled?: boolean;
  distribution: DemographicDistribution[];
  isLoading?: boolean;
  onChange: (distribution: DemographicDistribution[]) => void;
  title?: string;
}

const isEquallyDistributed = (items: DemographicDistribution[]): boolean => {
  if (items.length <= 1) return true;
  const base = Math.floor(100 / items.length);
  return items.every(
    (item) =>
      Math.round(item.weight * 100) === base ||
      Math.round(item.weight * 100) === base + 1
  );
};

const equalWeights = (count: number): number[] => {
  const base = Math.floor(100 / count);
  const remainder = 100 - base * count;
  return Array.from(
    { length: count },
    (_, i) => (base + (i < remainder ? 1 : 0)) / 100
  );
};

const DemographicSettingEditor: FC<DemographicSettingEditorProps> = ({
  availableOptions,
  disabled = false,
  distribution,
  isLoading = false,
  onChange,
  title,
}) => {
  const { i18n } = useLocalization();
  const [weightEditorOpen, setWeightEditorOpen] = useState(false);

  const selectedValues = useMemo(
    () => new Set(distribution.map((d) => d.value as DemographicValueValue)),
    [distribution]
  );

  const toggle = useCallback(
    (option: DemographicValue) => {
      if (disabled) return;
      const value = option.value as DemographicValueValue;
      const wasEqual = isEquallyDistributed(distribution);

      if (selectedValues.has(value)) {
        const next = distribution.filter(
          (d) => (d.value as DemographicValueValue) !== value
        );
        if (next.length === 0) {
          onChange(next);
        } else if (wasEqual) {
          const weights = equalWeights(next.length);
          onChange(next.map((d, i) => ({ ...d, weight: weights[i] })));
        } else {
          const removed = distribution.find(
            (d) => (d.value as DemographicValueValue) === value
          );
          const freed = removed?.weight ?? 0;
          const maxIdx = next.reduce(
            (mi, d, i, a) => (d.weight > a[mi].weight ? i : mi),
            0
          );
          onChange(
            next.map((d, i) => ({
              ...d,
              weight: i === maxIdx ? d.weight + freed : d.weight,
            }))
          );
        }
      } else if (wasEqual || distribution.length === 0) {
        const count = distribution.length + 1;
        const weights = equalWeights(count);
        const all = [
          ...distribution,
          { label: option.label, value: option.value, weight: 0 },
        ];
        onChange(all.map((d, i) => ({ ...d, weight: weights[i] })));
      } else {
        const share = 0.01;
        const maxIdx = distribution.reduce(
          (mi, d, i, a) => (d.weight > a[mi].weight ? i : mi),
          0
        );
        onChange([
          ...distribution.map((d, i) => ({
            ...d,
            weight: i === maxIdx ? d.weight - share : d.weight,
          })),
          { label: option.label, value: option.value, weight: share },
        ]);
      }
    },
    [disabled, distribution, onChange, selectedValues]
  );

  const canEditWeights = distribution.length >= 1 && !disabled;
  const otherPct = Math.max(
    0,
    100 - distribution.reduce((sum, d) => sum + Math.round(d.weight * 100), 0)
  );

  if (isLoading) {
    return (
      <Box>
        <Skeleton className="h-4 w-24 rounded mb-3" />
        <HStack className="flex-wrap gap-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton className="h-7 w-20 rounded-full" key={i} />
          ))}
        </HStack>
      </Box>
    );
  }

  return (
    <Box>
      <HStack className="flex-wrap gap-2">
        {availableOptions.map((option) => {
          const value = option.value as DemographicValueValue;
          const isActive = selectedValues.has(value);
          return (
            <Pressable
              className={
                disabled ? "data-[disabled=true]:opacity-100" : undefined
              }
              disabled={disabled}
              key={option.label}
              onPress={() => toggle(option)}
            >
              <Box
                className={`rounded-full px-3 py-1.5 ${
                  isActive ? "bg-primary-500" : "bg-background-100"
                } ${disabled ? "cursor-default" : ""}`}
              >
                <Text
                  className={`text-xs font-medium ${
                    isActive ? "text-white" : "text-typography-500"
                  }`}
                >
                  {option.label}
                </Text>
              </Box>
            </Pressable>
          );
        })}
      </HStack>
      {distribution.length > 0 && (
        <HStack className="flex-wrap gap-1.5 mt-3">
          {distribution.map((d) => {
            const value = d.value as DemographicValueValue;
            const pct = Math.round(d.weight * 100);
            return (
              <HStack
                className="items-center gap-1 px-2.5 py-1 rounded-full bg-background-50 border border-outline-200"
                key={d.label}
              >
                <Text className="text-xs text-typography-600">{d.label}</Text>
                <Box className="w-px h-3 bg-outline-200 mx-0.5" />
                {canEditWeights ? (
                  <Pressable onPress={() => setWeightEditorOpen(true)}>
                    <Text className="text-xs font-semibold text-primary-600">
                      {i18n.numberToPercentage(pct, { precision: 0 })}
                    </Text>
                  </Pressable>
                ) : (
                  <Text className="text-xs font-semibold text-primary-600">
                    {i18n.numberToPercentage(pct, { precision: 0 })}
                  </Text>
                )}
                <Pressable
                  disabled={disabled}
                  onPress={() => {
                    const opt = availableOptions.find(
                      (o) => (o.value as DemographicValueValue) === value
                    );
                    if (opt) toggle(opt);
                  }}
                >
                  <X color="rgb(var(--color-typography-400))" size={12} />
                </Pressable>
              </HStack>
            );
          })}
          {otherPct > 0 && (
            <HStack className="items-center gap-1 px-2.5 py-1 rounded-full bg-background-50 border border-dashed border-outline-200">
              <Text className="text-xs text-typography-400">
                {i18n.t("misc.otherValues")}
              </Text>
              <Box className="w-px h-3 bg-outline-200 mx-0.5" />
              <Text className="text-xs font-semibold text-typography-400">
                {i18n.numberToPercentage(otherPct, { precision: 0 })}
              </Text>
            </HStack>
          )}
        </HStack>
      )}

      {title && (
        <DemographicDistributionEditor
          distribution={distribution}
          onChange={onChange}
          onOpenChange={setWeightEditorOpen}
          open={weightEditorOpen}
          title={title}
        />
      )}
    </Box>
  );
};

export default DemographicSettingEditor;
